import { ElementRef, inject, TemplateRef, ViewContainerRef } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { fromEvent, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CellPreparedEvent } from 'devextreme/ui/data_grid';
import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';

export class ItemsTableHeaderDebugService {
  private overlayRef: OverlayRef | null;
  private overlay = inject(Overlay);
  private overlaySubscription: Subscription;
  private positions: ConnectedPosition[] = [{
    originX: 'end', originY: 'bottom',
    overlayX: 'end', overlayY: 'top',
  }];

  open(origin: HTMLElement, template: TemplateRef<any>, vcr: ViewContainerRef, context: any): void {
    this.close();

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(this.positions);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    this.overlayRef.attach(new TemplatePortal(template, vcr, {$implicit: context}));
    this.setCloseSubscription(origin);
  }

  private setCloseSubscription(origin?: HTMLElement): void {
    this.overlaySubscription = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => {
          const clickTarget = event.target as HTMLElement;
          const overlayClicked = !!this.overlayRef && this.overlayRef.overlayElement.contains(clickTarget);
          const originClicked = !!origin && origin.contains(clickTarget);
          return !!this.overlayRef && !overlayClicked && !originClicked;
        }),
        take(1)
      ).subscribe(() => this.close());
  }

  close(): void {
    this.overlaySubscription?.unsubscribe();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  closeAndRemoveColumnDebugIndicator(elementRef: ElementRef): void {
    this.close();
    elementRef.nativeElement
      .querySelectorAll('.c-items-table__column-debug')
      .forEach((el: HTMLElement) => el.remove());
  }

  isOpen(): boolean {
    return !!this.overlayRef;
  }

  addColumnDebugIndicator(
    e: CellPreparedEvent,
    template: TemplateRef<any>,
    vcr: ViewContainerRef,
    field: CustomFieldModel | undefined,
    fieldDebugEnabled: boolean,
  ): void {
    const cellElement = e.cellElement;
    if (!cellElement) {
      return;
    }
    cellElement.querySelectorAll('.c-items-table__column-debug').forEach(el => el.remove());
    if (!fieldDebugEnabled || !e.column?.dataField) {
      return;
    }
    if (!field?.columnName && !field?.dbName) {
      return;
    }
    const columnIndicators = cellElement.querySelector('.dx-column-indicators');
    if (!columnIndicators) {
      return;
    }

    columnIndicators.querySelectorAll('.c-items-table__column-debug-icon').forEach(iconEl => iconEl.remove());

    const icon = document.createElement('span');
    icon.classList.add('c-items-table__column-debug-icon');
    icon.setAttribute('data-column-id', e.column.dataField);
    icon.style.visibility = 'hidden';
    const iconImage = document.createElement('img');
    iconImage.src = 'assets/images/question-mark.svg';
    icon.appendChild(iconImage);

    const freezeIcon = columnIndicators.querySelector('.c-items-table-header-icon-freeze');
    if (freezeIcon) {
      columnIndicators.insertBefore(icon, freezeIcon);
    } else {
      columnIndicators.appendChild(icon);
    }

    const openDebugPopover = (origin: HTMLElement) => {
      this.close();
      this.open(
        origin,
        template,
        vcr,
        {path: field.dbName ?? e.column.dataField, columnName: field.columnName ?? ''}
      );
    };

    const showIcon = () => icon.style.visibility = 'visible';
    const hideIcon = () => {
      if (!this.isOpen()) {
        icon.style.visibility = 'hidden';
      }
    };

    e.cellElement.addEventListener('mouseenter', showIcon);
    e.cellElement.addEventListener('mouseleave', hideIcon);
  }
}
